import { fg } from '@confluence/feature-gating';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { PAGE_TREE_STATUSES } from '@confluence/page-tree/entry-points/pageTreeStatuses.ts';
import { preloadPageTree } from '@confluence/page-tree/entry-points/preloadPageTree.ts';

import ContentIdByReferenceIdQuery, {
	type ContentIdByReferenceIdQuery$data,
	type ContentIdByReferenceIdQuery$variables,
} from './graphql/__generated__/ContentIdByReferenceIdQuery.graphql.ts';
import WhiteboardUnifiedQuery, {
	type WhiteboardUnifiedQuery$data,
	type WhiteboardUnifiedQuery$variables,
} from './graphql/__generated__/WhiteboardUnifiedQuery.graphql.ts';
import SpaceQuery, {
	type SpaceQuery$data,
	type SpaceQuery$variables,
} from './graphql/__generated__/SpaceQuery.graphql.ts';

type PreloadWhiteboardDataParams = {
	spaceKey: string;
	contentId: string;
	skipPageTree?: boolean;
};

export async function preloadWhiteboardData({
	spaceKey,
	contentId,
	skipPageTree = false,
}: PreloadWhiteboardDataParams): Promise<any> {
	const preloadPromises = [preloadContentData(contentId, spaceKey), preloadSpace(spaceKey)];
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && !skipPageTree) {
		preloadPromises.push(
			preloadPageTree({
				spaceKey,
				statuses: PAGE_TREE_STATUSES,
				isSuperAdmin: false,
				contentId,
			}),
		);
	}
	return waitForAllReturnWhenError(preloadPromises);
}

async function preloadContentData(contentId: string, spaceKey: string): Promise<void> {
	const UUIDRegex = /[-]/;
	const isUuidBasedId = UUIDRegex.test(contentId);
	if (isUuidBasedId) {
		await query<ContentIdByReferenceIdQuery$data, ContentIdByReferenceIdQuery$variables>({
			query: ContentIdByReferenceIdQuery,
			variables: {
				id: contentId,
			},
		});
	} else {
		await query<WhiteboardUnifiedQuery$data, WhiteboardUnifiedQuery$variables>({
			query: WhiteboardUnifiedQuery,
			variables: {
				contentId,
				spaceKey:
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					spaceKey === '_' && fg('cc-cns-28262-fix-bad-space-key-preload') ? null : spaceKey,
				// The DSP query doesn't currently user the content version value, so using a fixed value
				contentVersion: 1,
			},
		});
	}
}

async function preloadSpace(spaceKey: string): Promise<void> {
	await query<SpaceQuery$data, SpaceQuery$variables>({
		query: SpaceQuery,
		variables: {
			spaceKey,
		},
	});
}
